import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { MulticamSet, deleteMulticamSet } from '../../graphqlService';
import { useAuth } from '../../context/AuthContext';
import { getBackendErrorMessage } from '../../utils/apolloError';
import { pluralize } from '../../utils/format';
import StatusChip from '../ui/StatusChip';
import LibraryCard from '../Library/LibraryCard';
import { formatRunningTime } from '../Library/libraryFormat';
import { getTimelineDuration } from './multicamTime';

const DELETE_FALLBACK_ERROR = 'Failed to delete multicam set. Please try again.';

/**
 * When a set was created, in ms. Current deployments send ISO strings; older
 * ones sent a millisecond timestamp as a string ("1727259600000"). Null when
 * the value can't be read.
 */
export const multicamCreatedTime = (value?: string | number | null): number | null => {
  if (value === null || value === undefined) return null;
  const raw = typeof value === 'string' ? value.trim() : value;
  if (raw === '') return null;
  const date = typeof raw === 'number' || /^-?\d+$/.test(raw)
    ? new Date(Number(raw))
    : new Date(raw);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Card date ("Sep 25, 2026"), or null when the value can't be read, so the
 * card never shows "Invalid Date".
 */
export const formatMulticamDate = (value?: string | number | null): string | null => {
  const time = multicamCreatedTime(value);
  if (time === null) return null;
  return new Date(time).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

interface MulticamCardProps {
  multicamSet: MulticamSet;
  onDeleted?: () => void;
}

const MulticamCard: React.FC<MulticamCardProps> = ({ multicamSet, onDeleted }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dialogTitleId = useId();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const angles = multicamSet.angles || [];
  const name = multicamSet.name || 'Untitled multicam set';
  // The poster comes from the master angle (A).
  const masterAngle = angles.find(a => a.angleKey === 'A');
  // A submitted set is read-only: it opens for viewing and can't be changed.
  const locked = multicamSet.status === 'LOCKED';
  // Only when every angle's length is known; otherwise it would understate.
  const lengthKnown = angles.length > 0 && angles.every(a => (a.sourceOutMs ?? a.durationMs) > 0);
  const lengthMs = lengthKnown ? getTimelineDuration(angles) : 0;

  const handleDeleteConfirm = async () => {
    if (!user) return;

    setDeleting(true);
    setError(null);

    try {
      const result = await deleteMulticamSet(multicamSet.id);
      if (result && result.success === false) {
        // Keep the card: the set was not deleted.
        setError(result.message || DELETE_FALLBACK_ERROR);
        return;
      }
      setDeleteDialogOpen(false);
      if (onDeleted) {
        onDeleted();
      }
    } catch (err: any) {
      console.error('Failed to delete multicam set:', err);
      setError(getBackendErrorMessage(err, DELETE_FALLBACK_ERROR));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setError(null);
  };

  return (
    <>
      <LibraryCard
        title={name}
        meta={[
          pluralize(angles.length, 'angle'),
          lengthMs > 0 ? formatRunningTime(lengthMs) : null,
          formatMulticamDate(multicamSet.createdAt),
        ]}
        posterSrc={masterAngle?.videoUrl}
        status={<StatusChip kind="multicam" status={multicamSet.status} />}
        openLabel={locked ? 'View' : 'Annotate'}
        openTo={`/annotate-multicam/${multicamSet.id}`}
        actions={[
          {
            label: 'Edit set-up',
            icon: <EditOutlinedIcon fontSize="small" />,
            onClick: () => navigate(`/multicam/${multicamSet.id}/edit`),
            disabled: locked,
          },
          {
            label: 'Delete',
            icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
            onClick: () => {
              setError(null);
              setDeleteDialogOpen(true);
            },
            disabled: locked,
            destructive: true,
          },
        ]}
        testId={`multicam-card-${multicamSet.id}`}
        openTestId={`annotate-multicam-${multicamSet.id}`}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        aria-labelledby={dialogTitleId}
      >
        <DialogTitle id={dialogTitleId} sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          Delete “{name}”?
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            This deletes the set and its notes, and can't be undone. The videos stay in your library.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete set'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MulticamCard;
